/**
 * Global Color Constants
 * Centralized color management for the Caready Conductor application
 * 
 * This file provides a single source of truth for all colors used throughout the application.
 * Colors are organized by category for better maintainability.
 */

export const COLORS = {
  // Brand Colors
  BRAND: {
    PRIMARY: '#CF1701',
    SECONDARY: '#687083',
    THIRD: '#092540',
    SUCCESS: '#2AB95E',
    BACKGROUND: '#FAFAFA',
  },

  // Text Colors
  TEXT: {
    MUTED: '#646566',
    DARK: '#1E1E1F',
    LIGHT: '#9AA2B1',
    WHITE: '#FFFFFF',
    BLACK: '#000000',
  },

  // Background Colors
  BACKGROUND: {
    LIGHT: '#F5F6F7',
    LIGHTER: '#F9FAFB',
    TAG: '#F0F2F5',
    HIGHLIGHT: '#FAE8E6',
    PAUSE: '#FFEBEC',
    WHITE: '#FFFFFF',
  },

  // Border Colors
  BORDER: {
    LIGHT: '#DCDDE0',
    MEDIUM: '#D1D5DC',
    DIVIDER: '#E4E7EB',
  },

  // Special Colors
  SPECIAL: {
    NIPL: '#FF6A39',
    ERROR: '#FF4D4D',
    WARNING: '#FF6A39',
  },

  // Status Colors
  STATUS: {
    PENDING: '#F5F6F7',
    ACTIVE: '#CF1701',
    SUCCESS: '#2AB95E',
    ERROR: '#FF4D4D',
    WARNING: '#FF6A39',
  },

  // Overlay Colors
  OVERLAY: {
    DARK: 'rgba(0,0,0,0.1)',
    LIGHT: 'rgba(255,255,255,0.9)',
  },
};

/**
 * Color utility functions
 */
export const ColorUtils = {
  /**
   * Get color by category and name
   * @param {string} category - Color category (e.g., 'BRAND', 'TEXT', 'BACKGROUND')
   * @param {string} name - Color name (e.g., 'PRIMARY', 'MUTED', 'LIGHT')
   * @returns {string} Color value
   */
  get: (category, name) => {
    return COLORS[category]?.[name] || '#000000';
  },

  /**
   * Get brand color
   * @param {string} name - Brand color name
   * @returns {string} Brand color value
   */
  brand: (name) => ColorUtils.get('BRAND', name),

  /**
   * Get text color
   * @param {string} name - Text color name
   * @returns {string} Text color value
   */
  text: (name) => ColorUtils.get('TEXT', name),

  /**
   * Get background color
   * @param {string} name - Background color name
   * @returns {string} Background color value
   */
  background: (name) => ColorUtils.get('BACKGROUND', name),

  /**
   * Get border color
   * @param {string} name - Border color name
   * @returns {string} Border color value
   */
  border: (name) => ColorUtils.get('BORDER', name),

  /**
   * Get special color
   * @param {string} name - Special color name
   * @returns {string} Special color value
   */
  special: (name) => ColorUtils.get('SPECIAL', name),

  /**
   * Get status color
   * @param {string} name - Status color name
   * @returns {string} Status color value
   */
  status: (name) => ColorUtils.get('STATUS', name),

  /**
   * Get overlay color
   * @param {string} name - Overlay color name
   * @returns {string} Overlay color value
   */
  overlay: (name) => ColorUtils.get('OVERLAY', name),
};

export default COLORS;